import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { YmmsDropdownsV2 } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/mmy-tab/ymms-dropdowns-v2';
import { MmyDropdowns } from 'client/site-modules/shared/components/mmy-dropdowns/mmy-dropdowns';
import { buildNoSubmodelVehiclePath, VehicleModel } from 'client/data/models/vehicle-v2';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { isEmpty, noop } from 'lodash-es';
import { PUB_STATES } from 'client/constants/pub-states';
import {
  useAppraisalTabsConfig,
  useAppraisalTabsState,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';
import { AppraisalTabsSubmitButton } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tabs-submit-button';
import {
  appraisalTabViewPropType,
  getResolvedAppraisalTabViewProps,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tab-view';
import { AppraiseTabContentFormWrapper } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraise-tab-content-form-wrapper';
import { useMmySubmit } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-mmy-submit';
import { useVehicleSelection } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-vehicle-selection';

const ERROR_MESSAGES = {
  make: 'Please select a make',
  model: 'Please select a model',
  year: 'Please select a year',
};

const DROPDOWNS_PLACEHOLDERS = {
  year: 'Select year',
  make: 'Select make',
  model: 'Select model',
};

const DROPDOWNS_LABELS = {
  year: 'Year',
  make: 'Make',
  model: 'Model',
};

const DROPDOWNS_PUB_STATES = [PUB_STATES.USED, PUB_STATES.NEW_USED];

function MmyTabContentUIBase({
  isFullWidth = false,
  ctaText = 'Appraise My Car',
  btnClassName = null,
  btnTrackingId = null,
  creativeId = undefined,
  query = {},
  vehicle = {},
  skipValidation = false,
  isYearDropdownFirst = true,
  initialVehicle = null,
  handlePrepopulatedVehicle = noop,
  dropdownPlaceholders = DROPDOWNS_PLACEHOLDERS,
  dropdownLabels = DROPDOWNS_LABELS,
  isActiveTab = false,
  view = null,
}) {
  const { isProjectEveStyle, isBackgroundStyleTabEnabled } = useAppraisalTabsConfig();
  const { mmy, isReadyForRedirect } = useAppraisalTabsState();
  const { isFullWidth: resolvedIsFullWidth } = getResolvedAppraisalTabViewProps({ view, isFullWidth });

  const { validate, validated, resetValidation } = useMmySubmit({ creativeId });
  const { onChange, prepopulatedVehicle } = useVehicleSelection({
    initialVehicle,
    vehicle,
    query,
    handlePrepopulatedVehicle,
    onVehicleChange: resetValidation,
  });

  const { make, model, year } = mmy || {};
  const selectedVehicle = useMemo(
    () => (isEmpty(mmy) ? prepopulatedVehicle : { year, makeSlug: make, modelSlug: model }),
    [make, mmy, model, prepopulatedVehicle, year]
  );

  const displayError = {
    make: validated && !make,
    model: validated && !model,
    year: validated && !year,
  };

  const dropdownColSize = {
    year: { xs: 12, lg: 4 },
    make: { xs: 12, lg: 4 },
    model: { xs: 12, lg: 4 },
  };

  const commonDropdownsProps = {
    skipValidation,
    dropdownPlaceholders,
    dropdownLabels,
    labelClassName: 'size-12',
    includeNextYear: false,
    formGroupClassName: '',
    onChange,
    errorMessages: ERROR_MESSAGES,
    displayError,
    pubStates: DROPDOWNS_PUB_STATES,
    prepopulatedVehicle: selectedVehicle,
    isFormControlErrorHighlighted: true,
    errorClassName: 'mb-0 mt-0_5',
    disabled: isReadyForRedirect,
    ...(isProjectEveStyle || isBackgroundStyleTabEnabled
      ? {
          dropdownColSize,
          className: 'd-lg-flex justify-space-lg-between',
          rowClassName: 'flex-grow-1 pe-lg-1',
        }
      : {}),
  };

  const appraisalButton = (
    <AppraisalTabsSubmitButton
      onSubmit={validate}
      buttonClassName={classnames(
        'py-0_75 btn-responsive-lg-down mt-1',
        btnClassName,
        isBackgroundStyleTabEnabled || isProjectEveStyle ? 'mt-lg-1_75' : 'mt-lg-0'
      )}
      data-test="appraise-btn"
      data-tracking-id={btnTrackingId}
      isDisabled={isReadyForRedirect}
      isSubmitting={isReadyForRedirect}
      isActiveTab={isActiveTab}
      ctaText={ctaText}
    />
  );

  return (
    <div className="appraise-by-mmy-tab">
      <AppraiseTabContentFormWrapper isFullWidth={resolvedIsFullWidth}>
        {isYearDropdownFirst ? (
          <YmmsDropdownsV2
            useUniqId
            useStyledSelects
            hasModelsDropdown
            hasSubmodelsDropdown={false}
            hasStylesDropdown={false}
            isResetOnBfcache
            {...commonDropdownsProps}
          >
            {appraisalButton}
          </YmmsDropdownsV2>
        ) : (
          <MmyDropdowns {...commonDropdownsProps}>{appraisalButton}</MmyDropdowns>
        )}
      </AppraiseTabContentFormWrapper>
    </div>
  );
}

MmyTabContentUIBase.propTypes = {
  isFullWidth: PropTypes.bool,
  ctaText: PropTypes.string,
  btnClassName: PropTypes.string,
  btnTrackingId: PropTypes.string,
  creativeId: PropTypes.string,
  query: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
  }),
  vehicle: PropTypes.shape({
    id: PropTypes.number,
  }),
  skipValidation: PropTypes.bool,
  isYearDropdownFirst: PropTypes.bool,
  initialVehicle: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  handlePrepopulatedVehicle: PropTypes.func,
  dropdownPlaceholders: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
  }),
  dropdownLabels: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
  }),
  isActiveTab: PropTypes.bool,
  view: appraisalTabViewPropType,
};

export const MmyTabContentUI = React.memo(MmyTabContentUIBase);

export const stateToPropsConfig = {
  vehicle: bindToPath(({ query = {} }) => {
    const { make, model, year } = query;
    return make && model && year ? buildNoSubmodelVehiclePath({ make, model, year }) : '';
  }, VehicleModel),
};

export const MmyTab = connectToModel(MmyTabContentUI, stateToPropsConfig);
